<template>
  <div class="age-input">
    <div
      v-if="outerLabelValue"
      :id="labelId"
      class="outer-label"
      :class="{ 'outer-label--bound': !fields.outerLabelFullWidth }"
    >
      <TipTap v-model="outerLabelValue" mode="single" />
    </div>
    <BaseInputShell v-bind="shellProps" :error="showError">
      <BaseInputField
        :id="inputId"
        name="age"
        inputmode="numeric"
        autocomplete="one-time-code"
        :modelValue="displayValue"
        @update:modelValue="onInput"
        @blur="handleBlur"
        @submit="handleSubmit"
        v-model:inner-label-data="innerLabelData"
        inner-label-fallback="years"
        :placeholder="placeholderText"
        :autofocus="fields.autoFocus"
        :aria-labelledby="outerLabelValue ? labelId : undefined"
        :aria-describedby="describedBy(showValidationMessage && errorId)"
        :aria-invalid="showError"
        v-bind="fieldProps"
      />
    </BaseInputShell>
    <div v-if="showValidationMessage" :id="errorId" class="validation-message">
      <TipTap v-model="validationMessageValue" mode="single" />
    </div>
    <div
      v-if="fields.showSuccessMessage && !showError && successMessageValue"
      class="success-message"
    >
      <TipTap v-model="successMessageValue" mode="single" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, watch, useId } from 'vue'
import { usePipe } from '@render/composables/usePipe'
import { useFormInput } from '@render/composables/useFormInput'
import { useInputAria } from '@render/composables/useInputAria'
import { useInputValidation } from '@render/composables/useInputValidation'
import { useComponentScopedVariables } from '@render/composables/useComponentScopedVariables'
import {
  getColorStyleData,
  getSizeStyleData,
  getSpacingStyleData,
} from '@render/composables/useStyleData'
import { tiptapToText } from '@render/lib/tiptapToText'
import { parseNumericVar } from '@render/lib/parseNumericVar'
import BaseInputShell from '../BaseInput/BaseInputShell.vue'
import BaseInputField from '../BaseInput/BaseInputField.vue'
import TipTap from '@render/lib/TipTap'
import type { AgeInputFields } from './definition'

const emit = defineEmits<{
  (e: 'onSubmit'): void
}>()

const fields = defineModel<AgeInputFields>('fields', {
  required: true,
  default: () => ({
    valueVariable: undefined,
    isValidVariable: undefined,
    outerLabel: undefined,
    placeholder: undefined,
    outerLabelFullWidth: false,
    outerLabelColor: undefined,
    outerLabelFontSize: undefined,
    outerLabelLineHeight: undefined,
    outerLabelFontWeight: undefined,
    outerLabelTextAlign: undefined,
    outerLabelMarginBottom: undefined,
    innerLabel: undefined,
    validationMessage: undefined,
    successMessage: undefined,
    showSuccessMessage: false,
    required: undefined,
    min: undefined,
    max: undefined,
    bgColor: undefined,
    bgColorHover: undefined,
    bgColorActive: undefined,
    bgColorError: undefined,
    bgColorDisabled: undefined,
    borderColor: undefined,
    borderColorHover: undefined,
    focusBorderColor: undefined,
    borderColorError: undefined,
    borderColorDisabled: undefined,
    borderRadius: undefined,
    textColor: undefined,
    textColorPlaceholder: undefined,
    textColorDisabled: undefined,
    labelColor: undefined,
    labelColorDisabled: undefined,
    labelFontSize: undefined,
    labelLineHeight: undefined,
    labelFontWeight: undefined,
    numbersModeFontSize: undefined,
    numbersModeLineHeight: undefined,
    numbersModeFontWeight: undefined,
    minHeight: undefined,
    maxWidth: undefined,
    padding: undefined,
  }),
})

const pipe = usePipe()
const inputId = `${useId()}-age`

const age = computed(() =>
  parseNumericVar(pipe.getData(`var.${fields.value.valueVariable?.key ?? ''}`)),
)

const displayValue = computed<number | string>(() =>
  age.value === undefined ? '' : Math.round(age.value),
)

const placeholderText = computed(() =>
  tiptapToText(pipe.getTranslation(fields.value.placeholder?.key)),
)

const innerLabelData = computed({
  get: () => pipe.getTranslation(fields.value.innerLabel?.key),
  set: (val: object) => {
    const key = fields.value.innerLabel?.key
    if (key) pipe.setTranslation(key, val)
  },
})

// Validation runs against rounded value to match integer UI value and integer min/max from admin
const validation = useInputValidation(
  () => (age.value === undefined ? undefined : Math.round(age.value)),
  () => ({
    required: fields.value.required,
    min: fields.value.min,
    max: fields.value.max,
  }),
)

const formInput = useFormInput({
  validate: () => validation.isValid.value,
  disableRealtimeValidation: () => fields.value.disableRealtimeValidation === true,
  persistKeys: () => [fields.value.valueVariable?.key, fields.value.isValidVariable?.key],
})

const showError = computed(() => formInput.showErrors.value && !validation.isValid.value)
// The author's label and validation copy sit OUTSIDE the <label> shell, so they
// are associated by id rather than by wrapping — see `useInputAria`.
const { labelId, errorId, describedBy } = useInputAria()
const showValidationMessage = computed(() => showError.value && !!validationMessageValue.value)

watch(
  validation.isValid,
  (valid) => {
    if (fields.value.isValidVariable?.key) {
      // Transient validity flag — reactive for CTA state, persisted on submit.
      pipe.setData(`var.${fields.value.isValidVariable.key}`, valid, { persist: false })
    }
  },
  { immediate: true },
)

const outerLabelValue = computed({
  get: () => pipe.getTranslation(fields.value.outerLabel?.key),
  set: (val: object) => {
    const key = fields.value.outerLabel?.key
    if (key) pipe.setTranslation(key, val)
  },
})

const validationMessageValue = computed({
  get: () => pipe.getTranslation(fields.value.validationMessage?.key),
  set: (val: object) => {
    const key = fields.value.validationMessage?.key
    if (key) pipe.setTranslation(key, val)
  },
})

const successMessageValue = computed({
  get: () => pipe.getTranslation(fields.value.successMessage?.key),
  set: (val: object) => {
    const key = fields.value.successMessage?.key
    if (key) pipe.setTranslation(key, val)
  },
})

function onInput(val: number | string) {
  const key = fields.value.valueVariable?.key
  if (!key) return
  // Transient: live in the reactive store, persisted on form submit.
  pipe.setData(`var.${key}`, val === '' ? null : Number(val), { persist: false })
}

function handleBlur() {
  // Preserve the existing "blur only flips touched when the field has content"
  // heuristic via useInputValidation, then route through useFormInput so the
  // disableRealtimeValidation field is honored.
  validation.onBlur()
  if (age.value !== undefined) formInput.handleBlur()
}

async function handleSubmit() {
  const { targetInvoked } = await formInput.requestSubmit()
  if (targetInvoked) return
  if (!validation.isValid.value) {
    validation.markBlurred()
    formInput.markTouched()
    return
  }
  emit('onSubmit')
}

useComponentScopedVariables('AgeInput', () => ({
  min: fields.value.min,
  max: fields.value.max,
  value: age.value,
}))

const overrideBgColor = computed(() => getColorStyleData(fields.value.bgColor))
const overrideBgColorHover = computed(() => getColorStyleData(fields.value.bgColorHover))
const overrideBgColorActive = computed(() => getColorStyleData(fields.value.bgColorActive))
const overrideBgColorError = computed(() => getColorStyleData(fields.value.bgColorError))
const overrideBgColorDisabled = computed(() => getColorStyleData(fields.value.bgColorDisabled))
const overrideBorderColor = computed(() => getColorStyleData(fields.value.borderColor))
const overrideBorderColorHover = computed(() => getColorStyleData(fields.value.borderColorHover))
const overrideFocusBorderColor = computed(() => getColorStyleData(fields.value.focusBorderColor))
const overrideBorderColorError = computed(() => getColorStyleData(fields.value.borderColorError))
const overrideBorderColorDisabled = computed(() =>
  getColorStyleData(fields.value.borderColorDisabled),
)
const overrideBorderRadius = computed(() => getSizeStyleData(fields.value.borderRadius))
const overrideTextColor = computed(() => getColorStyleData(fields.value.textColor))
const overrideTextColorPlaceholder = computed(() =>
  getColorStyleData(fields.value.textColorPlaceholder),
)
const overrideTextColorDisabled = computed(() => getColorStyleData(fields.value.textColorDisabled))
const overrideLabelColor = computed(() => getColorStyleData(fields.value.labelColor))
const overrideLabelColorDisabled = computed(() =>
  getColorStyleData(fields.value.labelColorDisabled),
)
const overrideLabelFontSize = computed(() => getSizeStyleData(fields.value.labelFontSize))
const overrideLabelLineHeight = computed(() => getSizeStyleData(fields.value.labelLineHeight))
const overrideNumbersModeFontSize = computed(() =>
  getSizeStyleData(fields.value.numbersModeFontSize),
)
const overrideNumbersModeLineHeight = computed(() =>
  getSizeStyleData(fields.value.numbersModeLineHeight),
)
const overrideOuterLabelColor = computed(
  () => getColorStyleData(fields.value.outerLabelColor) ?? 'var(--color-text-secondary)',
)
const overrideOuterLabelFontSize = computed(
  () => getSizeStyleData(fields.value.outerLabelFontSize) ?? 'var(--text-body-sm-size)',
)
const overrideOuterLabelLineHeight = computed(
  () => getSizeStyleData(fields.value.outerLabelLineHeight) ?? 'var(--text-body-sm-line-height)',
)
const overrideOuterLabelFontWeight = computed(
  () => fields.value.outerLabelFontWeight ?? 'var(--text-body-sm-weight)',
)
const overrideOuterLabelTextAlign = computed(() => fields.value.outerLabelTextAlign ?? 'left')
const overrideOuterLabelMarginBottom = computed(
  () => getSizeStyleData(fields.value.outerLabelMarginBottom) ?? 'var(--space-8)',
)
const overrideMinHeight = computed(() => getSizeStyleData(fields.value.minHeight))
const overrideMaxWidth = computed(() => getSizeStyleData(fields.value.maxWidth) ?? '100%')
const overridePadding = computed(() => getSpacingStyleData(fields.value.padding))

const shellProps = computed(() => ({
  bgColor: overrideBgColor.value,
  bgColorHover: overrideBgColorHover.value,
  bgColorActive: overrideBgColorActive.value,
  bgColorError: overrideBgColorError.value,
  bgColorDisabled: overrideBgColorDisabled.value,
  borderColor: overrideBorderColor.value,
  borderColorHover: overrideBorderColorHover.value,
  focusBorderColor: overrideFocusBorderColor.value,
  borderColorError: overrideBorderColorError.value,
  borderColorDisabled: overrideBorderColorDisabled.value,
  borderRadius: overrideBorderRadius.value,
  minHeight: overrideMinHeight.value,
  maxWidth: overrideMaxWidth.value,
  padding: overridePadding.value,
}))

const fieldProps = computed(() => ({
  textColor: overrideTextColor.value,
  textColorPlaceholder: overrideTextColorPlaceholder.value,
  textColorDisabled: overrideTextColorDisabled.value,
  labelColor: overrideLabelColor.value,
  labelColorDisabled: overrideLabelColorDisabled.value,
  labelFontSize: overrideLabelFontSize.value,
  labelLineHeight: overrideLabelLineHeight.value,
  labelFontWeight: fields.value.labelFontWeight,
  numbersModeFontSize: overrideNumbersModeFontSize.value,
  numbersModeLineHeight: overrideNumbersModeLineHeight.value,
  numbersModeFontWeight: fields.value.numbersModeFontWeight,
}))
</script>

<style scoped lang="scss">
.age-input {
  max-width: 100%;
  margin-left: auto;
  margin-right: auto;
}

.outer-label {
  margin-bottom: v-bind(overrideOuterLabelMarginBottom);
  color: v-bind(overrideOuterLabelColor);
  font-size: v-bind(overrideOuterLabelFontSize);
  font-weight: v-bind(overrideOuterLabelFontWeight);
  line-height: v-bind(overrideOuterLabelLineHeight);
  text-align: v-bind(overrideOuterLabelTextAlign);
}

.outer-label--bound {
  max-width: v-bind(overrideMaxWidth);
  margin-left: auto;
  margin-right: auto;
}

.validation-message {
  margin-top: var(--space-4);
  color: var(--color-feedback-error-text);
  font-size: var(--text-body-sm-size);
  line-height: var(--text-body-sm-line-height);
  text-align: center;
}

.success-message {
  margin-top: var(--space-4);
  color: var(--color-feedback-success-text);
  font-size: var(--text-body-sm-size);
  line-height: var(--text-body-sm-line-height);
  text-align: center;
}
</style>
